import { useState } from "react";

// Tipos de alerta que podemos manejar
export enum AlertType {
  CONFIRMATION, // Muestra "Cancel" y "Confirm"
  ERROR, // Muestra solo "Accept"
  INFO, // Muestra solo "Accept"
}

// Estructura base para todas las alertas
interface BaseAlert {
  open: boolean;
  title: string;
  message: string;
  alertType: AlertType;
}

// Alerta de confirmación (con callback de confirmación)
interface ConfirmationAlert extends BaseAlert {
  alertType: AlertType.CONFIRMATION;
  onConfirm: () => void;
}

// Alerta de error o información (sin callback adicional)
interface MessageAlert extends BaseAlert {
  alertType: AlertType.ERROR | AlertType.INFO;
}

export type AlertState = ConfirmationAlert | MessageAlert;

// Hook para gestionar diferentes tipos de alertas
export const useAlertModal = () => {
  const [alertState, setAlertState] = useState<AlertState>({
    open: false,
    title: "",
    message: "",
    alertType: AlertType.INFO,
  });

  // Muestra una alerta de confirmación (con botones Cancel/Confirm)
  const showConfirmation = (
    title: string,
    message: string,
    onConfirm: () => void,
  ) => {
    setAlertState({
      open: true,
      title,
      message,
      alertType: AlertType.CONFIRMATION,
      onConfirm,
    });
  };

  // Muestra una alerta de error (solo botón Accept)
  const showError = (
    title: string = "Sorry!",
    message: string = "Sorry, you cannot perform this action. Please contact your account administrator.",
  ) => {
    setAlertState({
      open: true,
      title,
      message,
      alertType: AlertType.ERROR,
    });
  };

  // Muestra una alerta informativa (solo botón Accept)
  const showInfo = (title: string, message: string) => {
    setAlertState({
      open: true,
      title,
      message,
      alertType: AlertType.INFO,
    });
  };

  // Cierra cualquier alerta
  const closeAlert = () => {
    setAlertState((prev) => ({ ...prev, open: false }));
  };

  // Confirma la acción (para alertas de confirmación)
  const confirmAction = () => {
    if (alertState.alertType === AlertType.CONFIRMATION) {
      alertState.onConfirm();
    }
    closeAlert();
  };

  return {
    alertState,
    showConfirmation,
    showError,
    showInfo,
    closeAlert,
    confirmAction,
  };
};
